import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface ProfileViewStats {
    totalViews: number;
    recentViewers: Array<{
        viewer: {
            id: string;
            name: string;
            profileImage?: string;
        } | null;
        viewedAt: string;
    }>;
    timeRange: string;
}

export const profileViewApi = createApi({
    reducerPath: 'profileViewApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${(import.meta.env.VITE_API_URL || 'http://localhost:5000').replace(/\/api$/, '')}/api/v1/profile-views`,
        credentials: 'include',
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('token');
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['ProfileViews'],
    endpoints: (builder) => ({
        recordProfileView: builder.mutation<
            { success: boolean; message: string; data: { viewRecorded: boolean } },
            string
        >({
            query: (profileOwnerId) => ({
                url: `/record/${profileOwnerId}`,
                method: 'POST',
            }),
            invalidatesTags: ['ProfileViews'],
        }),
        getProfileViewStats: builder.query<
            { success: boolean; data: ProfileViewStats; message: string },
            { profileOwnerId: string; timeRange?: string }
        >({
            query: ({ profileOwnerId, timeRange }) => ({
                url: `/stats/${profileOwnerId}${timeRange ? `?timeRange=${timeRange}` : ''}`,
            }),
            providesTags: ['ProfileViews'],
            keepUnusedDataFor: 60,
        }),
        getMyProfileViews: builder.query<
            { success: boolean; data: ProfileViewStats; message: string },
            { timeRange?: string }
        >({
            query: ({ timeRange }) => ({
                url: `/my-stats${timeRange ? `?timeRange=${timeRange}` : ''}`,
            }),
            providesTags: ['ProfileViews'],
            keepUnusedDataFor: 60,
        }),
    }),
});

export const {
    useRecordProfileViewMutation,
    useGetProfileViewStatsQuery,
    useGetMyProfileViewsQuery,
} = profileViewApi;